import { ThemeEvents } from '@theme/events';

/**
 * Cart Drawer Upsells (Recommendations)
 * - Uses Shopify's recommendations endpoint per cart item (intent: related)
 * - Renders tiles below cart items inside the cart drawer
 * - Reuses existing Quick Add modal for variant selection and add-to-cart
 * - Debounces refreshes on cart updates; avoids duplicate products and items already in cart
 */
class CartDrawerUpsellsRecommendations {
  constructor() {
    /** @type {boolean} */
    this.initialized = false;
    /** @type {Map<number, any[]>} */
    this.recommendationsCacheByProductId = new Map();
    /** @type {Set<string>} */
    this.renderedHandles = new Set();
    /** @type {AbortController | null} */
    this.abortController = null;
    /** @type {number | undefined} */
    this._updateTimer = undefined;
    /** @type {number} */
    this.maxTiles = 10;
    /** @type {('related'|'complementary')} */
    this.intent = 'related';
    /** @type {MutationObserver | null} */
    this.mutationObserver = null;

    this.init();
  }

  init() {
    if (this.initialized) return;

    document.addEventListener(ThemeEvents.cartUpdate, this._debouncedRefresh, { passive: true });
    document.addEventListener('cart:updated', this._debouncedRefresh, { passive: true });
    document.addEventListener('cart-drawer:opened', this._debouncedRefresh, { passive: true });
    document.addEventListener('DOMContentLoaded', () => this.refresh(), { passive: true });

    // Watch for DOM changes that might hide cart items
    this.setupMutationObserver();

    // Initial render
    this.refresh();
    this.initialized = true;
  }

  setupMutationObserver() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (!cartDrawer) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const target = /** @type {HTMLElement} */ (mutation.target);
          if (target.classList.contains('cart-items__table') && target.style.display === 'none') {
            target.style.display = 'table';
          }
        }
        
        // Also watch for any DOM changes that might affect cart items
        if (mutation.type === 'childList') {
          const cartItemsTable = document.querySelector('.cart-items__table');
          const cartItemsWrapper = document.querySelector('.cart-drawer__items');
          
          if (cartItemsTable && cartItemsTable.style.display === 'none') {
            cartItemsTable.style.display = 'table';
          }
          
          if (cartItemsWrapper && cartItemsWrapper.style.display === 'none') {
            cartItemsWrapper.style.display = 'block';
          }
        }
      }
    });

    this.mutationObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['style'],
      childList: true,
      subtree: true
    });
  }

  _debouncedRefresh = () => {
    clearTimeout(this._updateTimer);
    this._updateTimer = window.setTimeout(() => this.refresh(), 150);
  };

  /** @returns {HTMLElement | null} */
  getContainer() {
    return document.querySelector('[data-cart-upsells-recommendations]');
  }

  /** Ensures upsell container exists (creates if missing) */
  ensureContainer() {
    let container = this.getContainer();
    if (container) return container;

    // Look for the cart drawer content area where we can safely insert upsells
    const cartDrawerContent = document.querySelector('.cart-drawer__content');
    if (!cartDrawerContent) {
      return null;
    }

    // Find the cart items wrapper (scroll-hint with class cart-drawer__items)
    const cartItemsWrapper = cartDrawerContent.querySelector('.cart-drawer__items');
    if (!cartItemsWrapper || !cartItemsWrapper.parentNode) {
      return null;
    }

    // Verify cart items are visible before proceeding
    const cartTable = cartItemsWrapper.querySelector('.cart-items__table');
    if (!cartTable) {
      return null;
    }

    container = document.createElement('div');
    container.setAttribute('data-cart-upsells-recommendations', '');
    container.className = 'cart-upsells-recommendations-container';

    const headerWrapper = document.createElement('cart-drawer-addon-infinite');
    headerWrapper.setAttribute('data-enable-infinite-scroll', 'false');
    headerWrapper.innerHTML = `
      <div id="step-last-minute" class="cart-drawer-step">
        <span class="upsells-highlight">YOU MIGHT ALSO LIKE THESE SIMILAR PRODUCTS</span>
      </div>
    `;

    const grid = document.createElement('div');
    grid.className = 'cart-upsells-recommendations-grid';
    container.appendChild(headerWrapper);
    container.appendChild(grid);

          // Insert the upsell container as a sibling to the cart items wrapper, not as a child of cart-drawer__content
      // This prevents interference with the scroll-hint component
      if (cartItemsWrapper && cartItemsWrapper.parentNode) {
        cartItemsWrapper.parentNode.insertBefore(container, cartItemsWrapper.nextSibling);
        
        // Immediately show placeholder tiles to reserve space
        const grid = container.querySelector('.cart-upsells-recommendations-grid');
        if (grid) {
          this.showPlaceholderTiles(grid);
        }
      } else {
        // Fallback: insert before cart summary
        const cartSummary = cartDrawerContent.querySelector('.cart-drawer__summary');
        if (cartSummary) {
          cartDrawerContent.insertBefore(container, cartSummary);
        } else {
          cartDrawerContent.appendChild(container);
        }
        
        // Show placeholder tiles in fallback case too
        const grid = container.querySelector('.cart-upsells-recommendations-grid');
        if (grid) {
          this.showPlaceholderTiles(grid);
        }
      }

    // Immediately ensure cart items remain visible
    this.ensureCartItemsVisible();

    // Multiple checks to ensure cart items remain visible
    setTimeout(() => {
      this.ensureCartItemsVisible();
    }, 100);
    
    setTimeout(() => {
      this.ensureCartItemsVisible();
    }, 500);
    
    setTimeout(() => {
      this.ensureCartItemsVisible();
    }, 1000);

    return container;
  }

  async refresh() {
    const container = this.ensureContainer();
    if (!container) return;
    const grid = container.querySelector('.cart-upsells-recommendations-grid');
    if (!grid) return;

    const cart = await this.fetchCart();
    const cartHandles = new Set((cart?.items || []).map((i) => i.handle));
    const productIds = Array.from(new Set((cart?.items || []).map((i) => i.product_id).filter(Boolean)));
    
    if (productIds.length === 0) {
      // Show placeholder tiles instead of clearing completely
      this.showPlaceholderTiles(grid);
      this.injectStylesOnce();
      return;
    }

    // Aggregate recommendations across cart items
    const recommendedProducts = await this.fetchAllRecommendations(productIds, this.intent);

    // Dedupe by handle and exclude items already in cart
    const unique = [];
    const seen = new Set();
    for (const p of recommendedProducts) {
      const handle = p?.handle;
      if (!handle || seen.has(handle) || cartHandles.has(handle)) continue;
      seen.add(handle);
      unique.push(p);
      if (unique.length >= this.maxTiles) break;
    }

    // Smooth update: preserve existing tiles and update content
    this.updateTilesSmoothly(grid, unique);

    this.injectStylesOnce();
    
    // Ensure cart items remain visible after our changes
    this.ensureCartItemsVisible();
  }



  /** Show placeholder tiles to reserve space */
  showPlaceholderTiles(grid) {
    grid.innerHTML = '';
    for (let i = 0; i < this.maxTiles; i++) {
      const placeholderTile = document.createElement('div');
      placeholderTile.className = 'upsell-tile upsell-tile--placeholder';
      placeholderTile.innerHTML = `
        <div class="upsell-card upsell-card--placeholder">
          <div class="upsell-card__media upsell-card__media--placeholder"></div>
          <div class="upsell-card__title upsell-card__title--placeholder"></div>
          <div class="upsell-card__price upsell-card__price--placeholder"></div>
          <div class="upsell-card__quick-add upsell-card__quick-add--placeholder"></div>
        </div>
      `;
      grid.appendChild(placeholderTile);
    }
  }

  /** Update tiles smoothly without clearing the entire grid */
  updateTilesSmoothly(grid, products) {
    const existingTiles = Array.from(grid.children);
    const newTiles = [];
    
    // Build new tiles
    for (const product of products) {
      const tile = this.buildTile(product);
      newTiles.push(tile);
    }
    
    // Update rendered handles
    this.renderedHandles = new Set(products.map((p) => p.handle));
    
    // Smooth transition: fade out old tiles, fade in new ones
    if (existingTiles.length > 0) {
      existingTiles.forEach((tile, index) => {
        if (index < newTiles.length) {
          // Replace existing tile with new one
          tile.style.opacity = '0';
          setTimeout(() => {
            if (tile.parentNode) {
              tile.parentNode.replaceChild(newTiles[index], tile);
              newTiles[index].style.opacity = '1';
            }
          }, 50);
        } else {
          // Remove extra tiles
          tile.style.opacity = '0';
          setTimeout(() => tile.remove(), 100);
        }
      });
    }
    
    // Add any new tiles that don't have existing ones to replace
    for (let i = existingTiles.length; i < newTiles.length; i++) {
      newTiles[i].style.opacity = '0';
      grid.appendChild(newTiles[i]);
      setTimeout(() => {
        newTiles[i].style.opacity = '1';
      }, 100);
    }
  }

  /** Ensures cart items remain visible after any DOM manipulations */
  ensureCartItemsVisible() {
    const cartItemsTable = document.querySelector('.cart-items__table');
    const cartItemsWrapper = document.querySelector('.cart-drawer__items');
    const scrollHint = document.querySelector('scroll-hint.cart-drawer__items');
    
    if (cartItemsTable) {
      cartItemsTable.style.display = 'table';
      cartItemsTable.style.visibility = 'visible';
      cartItemsTable.style.width = '100%';
    }
    
    if (cartItemsWrapper) {
      cartItemsWrapper.style.display = 'block';
      cartItemsWrapper.style.visibility = 'visible';
      cartItemsWrapper.style.overflow = 'visible';
      cartItemsWrapper.style.maxHeight = 'none';
    }
    
    if (scrollHint) {
      scrollHint.style.overflow = 'visible';
      scrollHint.style.maxHeight = 'none';
    }
    
    // Also check the parent cart-drawer__content
    const cartDrawerContent = document.querySelector('.cart-drawer__content');
    if (cartDrawerContent) {
      cartDrawerContent.style.overflowY = 'auto';
      cartDrawerContent.style.maxHeight = 'calc(100vh - var(--header-height, 60px))';
    }
  }

  /** Cleanup method to remove event listeners and observers */  
  destroy() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    
    document.removeEventListener(ThemeEvents.cartUpdate, this._debouncedRefresh);
    document.removeEventListener('cart:updated', this._debouncedRefresh);
    document.removeEventListener('cart-drawer:opened', this._debouncedRefresh);
    // Note: DOMContentLoaded event listeners are automatically cleaned up after firing
    
    clearTimeout(this._updateTimer);
    this._updateTimer = undefined;
    
    this.initialized = false;
  }

  async fetchCart() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  /**
   * @param {number[]} productIds
   * @param {'related'|'complementary'} intent
   * @returns {Promise<any[]>}
   */
  async fetchAllRecommendations(productIds, intent) {
    const results = [];

    for (const id of productIds) {
      const items = await this.fetchRecommendations(id, intent);
      if (Array.isArray(items)) results.push(...items);
    }

    return results;
  }

  /**
   * @param {number} productId
   * @param {'related'|'complementary'} intent
   * @returns {Promise<any[] | null>}
   */
  async fetchRecommendations(productId, intent) {
    if (this.recommendationsCacheByProductId.has(productId)) {
      return this.recommendationsCacheByProductId.get(productId) || null;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const url = `/recommendations/products.json?product_id=${encodeURIComponent(String(productId))}&intent=${encodeURIComponent(intent)}&limit=8`;
      const res = await fetch(url, { signal: this.abortController.signal, credentials: 'same-origin' });
      if (!res.ok) return null;
      const data = await res.json();
      const products = Array.isArray(data?.products) ? data.products : [];
      this.recommendationsCacheByProductId.set(productId, products);
      return products;
    } catch (e) {
      if (/** @type {any} */(e)?.name === 'AbortError') return null;
      return null;
    } finally {
      this.abortController = null;
    }
  }

  /**
   * @param {any} product Recommendation product object (from /recommendations/products.json)
   */
  buildTile(product) {
    const productUrl = product?.url || `/products/${product?.handle}`;
    const firstImage = product?.featured_image?.url || product?.featured_image || product?.images?.[0]?.src || '';
    const variant = (product?.variants || [])[0] || {};
    const priceCents = Number(variant?.price || product?.price || 0) * (String(variant?.price || product?.price).includes('.') ? 100 : 1);
    // Always calculate compare at price as double of selling price
    const compareAtCents = priceCents * 2;
    const showCompare = compareAtCents > priceCents && compareAtCents > 0;

    const wrapper = document.createElement('div');
    wrapper.className = 'upsell-tile';
    wrapper.setAttribute('data-handle', product?.handle || '');

    wrapper.innerHTML = `
      <product-card class="upsell-card">
        <a href="${productUrl}" ref="productCardLink" class="upsell-card__link">
          <div class="upsell-card__media">
            ${firstImage ? `<img src="${firstImage}" alt="${this.escapeHtml(product?.title || '')}">` : ''}
          </div>
          <div class="upsell-card__title">${this.escapeHtml(product?.title || '')}</div>
          <div class="upsell-card__price">
            <div class="price-row">
              ${showCompare ? `<span class="upsell-card__price--compare">${this.formatMoney(compareAtCents)}</span>` : ''}
              <span class="upsell-card__price--actual">${this.formatMoney(priceCents)}</span>
            </div>
          </div>
        </a>
        <quick-add-component class="upsell-quick-add" data-product-title="${this.escapeHtml(product?.title || '')}">
          <button type="button" class="button quick-add__button quick-add__button--choose upsell-card__quick-add">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
              <path d="M3 12H21M21 12L14 19M21 12L14 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>Add
          </button>
        </quick-add-component>
      </product-card>
    `;

    const qaButton = wrapper.querySelector('.upsell-card__quick-add');
    if (qaButton) {
      qaButton.addEventListener('click', (e) => {
        const qa = /** @type {any} */ (wrapper.querySelector('quick-add-component'));
        if (qa && typeof qa.handleClick === 'function') {
          qa.handleClick(e);
        }
      });
    }

    document.addEventListener(
      ThemeEvents.cartUpdate,
      (ev) => {
        const source = /** @type {any} */ (ev)?.detail?.source;
        if (source === 'product-form' || source === 'quick-add') {
          wrapper.remove();
        }
      },
      { once: true }
    );

    return wrapper;
  }

  injectStylesOnce() {
    if (document.getElementById('cart-upsells-recommendations-styles')) return;
    const style = document.createElement('style');
    style.id = 'cart-upsells-recommendations-styles';
    style.textContent = `
      /* Ensure cart drawer content is scrollable */
      .cart-drawer__content {
        overflow-y: auto !important;
        max-height: calc(100vh - var(--header-height, 60px)) !important;
      }
      
      /* Ensure cart items remain visible and scrollable */
      .cart-drawer__items {
        display: block !important;
        overflow: visible !important;
      }
      
      .cart-items__table {
        display: table !important;
        width: 100% !important;
      }
      
      /* Upsell container styling */
      .cart-upsells-recommendations-container { 
        margin-top: 16px; 
        border-top: 1px solid var(--style-border-color, #e5e5e5);
        padding-top: 16px;
        background: var(--color-background, #ffffff);
      }
      
      .cart-upsells-recommendations-container .upsell-card { position: relative; }
      .cart-upsells-recommendations-container .upsell-card__media img { width: 100%; height: auto; display: block; border-radius: 8px; }
      .cart-upsells-recommendations-container .upsell-card__title { margin-top: 8px; font-size: 14px; line-height: 1.3; }
      .cart-upsells-recommendations-container .upsell-card__price { 
        display: flex; 
        flex-direction: row; 
        align-items: center;
        gap: 8px; 
        line-height: 1.2;
      }
      .cart-upsells-recommendations-container .upsell-card__price--compare { 
        text-decoration: line-through; 
        color: #7a7a7a; 
        font-size: 14px; 
        font-weight: 300;
      }
      .cart-upsells-recommendations-container .upsell-card__price--actual.black-friday-deal-price {
        background: linear-gradient(135deg, #FFB84D 0%, #FFE066 100%);
        color: #8B0000;
        font-size: 18px;
        font-weight: 700;
        padding: 6px 12px;
        border-radius: 8px;
        display: inline-block;
        line-height: 1.2;
      }
      .cart-upsells-recommendations-container .upsell-card__sale-label {
        color: #8B0000;
        font-size: 12px;
        font-weight: 600;
        margin-top: 4px;
        line-height: 1.2;
      }
      .cart-upsells-recommendations-container .upsell-card__price--savings { color: #2a7a1f; font-size: 12px; font-weight: 600; }
      
      /* Keep compare and actual prices on same line */
      .cart-upsells-recommendations-container .upsell-card__price .price-row {
        display: flex;
        align-items: baseline;
        gap: 8px;
      }
      .cart-drawer-step .upsells-highlight { color: #2a7a1f; }
      .cart-upsells-recommendations-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; padding: 16px; }
      @media (min-width: 750px) { .cart-upsells-recommendations-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
      
      /* Placeholder tiles to reserve space */
      .upsell-tile--placeholder {
        min-height: 200px;
        background: #f8f8f8;
        border-radius: 8px;
        opacity: 0.6;
      }
      
      .upsell-card--placeholder .upsell-card__media--placeholder {
        width: 100%;
        height: 120px;
        background: #e0e0e0;
        border-radius: 8px;
        margin-bottom: 8px;
      }
      
      .upsell-card--placeholder .upsell-card__title--placeholder {
        width: 80%;
        height: 16px;
        background: #e0e0e0;
        border-radius: 4px;
        margin-bottom: 8px;
      }
      
      .upsell-card--placeholder .upsell-card__price--placeholder {
        width: 60%;
        height: 14px;
        background: #e0e0e0;
        border-radius: 4px;
        margin-bottom: 8px;
      }
      
      .upsell-card--placeholder .upsell-card__quick-add--placeholder {
        width: 100%;
        height: 32px;
        background: #e0e0e0;
        border-radius: 16px;
      }
      
      /* Smooth transitions */
      .upsell-tile {
        transition: opacity 0.15s ease-in-out;
      }
      
      /* Fix for scroll-hint component */
      scroll-hint.cart-drawer__items {
        overflow: visible !important;
        max-height: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  /** @param {string} s */
  escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /** @param {number} cents */
  formatMoney(cents) {
    const amount = (Number(cents || 0) / 100);
    try {
      const locale = document.documentElement.lang || 'en-US';
      const currency = /** @type {any} */ (window).Shopify?.currency?.active || /** @type {any} */ (window).Shopify?.locale?.currency || 'USD';
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  }
}

function initializeCartDrawerUpsellsRecommendations() {
  // Clean up existing instance if it exists
  if (/** @type {any} */ (window).cartDrawerUpsellsRecommendations) {
    /** @type {any} */ (window).cartDrawerUpsellsRecommendations.destroy();
    delete /** @type {any} */ (window).cartDrawerUpsellsRecommendations;
  }
  
  // Wait for cart drawer to be fully initialized
  const cartDrawer = document.querySelector('cart-drawer-component');
  const cartItems = document.querySelector('.cart-drawer__items');
  const cartTable = document.querySelector('.cart-items__table');
  
  if (cartDrawer && cartItems && cartTable) {
    /** @type {any} */ (window).cartDrawerUpsellsRecommendations = new CartDrawerUpsellsRecommendations();
  } else {
    setTimeout(initializeCartDrawerUpsellsRecommendations, 500);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCartDrawerUpsellsRecommendations);
} else {
  initializeCartDrawerUpsellsRecommendations();
}

document.addEventListener(ThemeEvents.cartUpdate, () => {
  setTimeout(initializeCartDrawerUpsellsRecommendations, 100);
});

document.addEventListener('click', (e) => {
  const target = /** @type {any} */ (e.target);
  if (!target) return;
  const cartButton = target.closest?.('[on\\:click="/open"]');
  if (cartButton && cartButton.closest('cart-drawer-component')) {
    setTimeout(initializeCartDrawerUpsellsRecommendations, 400);
  }
});


